import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChipInput } from "@/components/ui/chip-input";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

export type GoalFieldValue = string | string[];
export type GoalFieldValues = Record<string, GoalFieldValue>;
type FieldType = "chips" | "single" | "text" | "link";
export interface GoalField { key: string; label: string; type: FieldType; options?: string[]; placeholder?: string; required?: boolean }

export const GOAL_FIELDS: Record<string, GoalField[]> = {
  sell: [
    { key: "pain", label: "Problem you solve for them", type: "chips", options: ["Cut costs", "Save time", "Faster delivery", "Better quality", "Reliable supply", "More leads / sales", "Compliance"] },
    { key: "proof", label: "Proof to mention", type: "chips", options: ["Known clients", "Years in business", "Certifications", "Case study result", "Awards"] },
  ],
  partners: [
    { key: "partner_type", label: "Partner type", type: "chips", required: true, options: ["Distributor", "Reseller", "Dealer", "Referral partner", "Integration partner", "Franchise"] },
    { key: "partner_gets", label: "What the partner gets", type: "chips", options: ["Good margins", "Exclusive territory", "Leads from us", "Training & support", "Marketing material"] },
  ],
  hiring: [
    { key: "role", label: "Role title", type: "text", required: true, placeholder: "e.g. Senior React Developer" },
    { key: "seniority", label: "Seniority", type: "single", options: ["Junior", "Mid", "Senior", "Lead / Manager"] },
    { key: "skills", label: "Key skills", type: "chips", placeholder: "Type a skill and press Enter" },
    { key: "work_mode", label: "Work mode", type: "single", options: ["Onsite", "Hybrid", "Remote"] },
    { key: "location", label: "Job location", type: "text", placeholder: "e.g. Pune" },
    { key: "experience", label: "Experience", type: "single", options: ["0-2 yrs", "2-5 yrs", "5-8 yrs", "8+ yrs"] },
    { key: "why_join", label: "Why join you", type: "chips", options: ["Fast growth", "Good pay", "Strong brand", "Remote friendly", "Learning", "ESOPs"] },
  ],
  event: [
    { key: "event_name", label: "Event name", type: "text", required: true, placeholder: "e.g. Plastics Expo 2026" },
    { key: "event_type", label: "Type", type: "single", options: ["Webinar", "Conference", "Booth at expo", "Meetup", "Workshop"] },
    { key: "date", label: "Date", type: "text", placeholder: "e.g. 12 Dec" },
    { key: "venue", label: "Online or venue city", type: "text", placeholder: "e.g. Online / Mumbai, Hall 4" },
    { key: "attendees", label: "Who should attend", type: "chips", options: ["Founders", "CXOs", "Plant heads", "Purchase heads", "HR leaders", "Marketing heads"] },
  ],
  networking: [
    { key: "meet", label: "Who you want to meet", type: "chips", required: true, options: ["Founders", "CXOs", "Peers in my industry", "Investors", "Industry experts", "Potential mentors"] },
    { key: "why", label: "Why", type: "chips", options: ["Learn", "Collaborate", "Referrals", "Visibility", "Partnerships"] },
    { key: "topics", label: "Shared topics", type: "chips", placeholder: "e.g. AI, manufacturing, D2C" },
  ],
  job_search: [
    { key: "role", label: "Target role", type: "text", required: true, placeholder: "e.g. Product Manager" },
    { key: "seniority", label: "Seniority", type: "single", options: ["Entry", "Mid", "Senior", "Leadership"] },
    { key: "industries", label: "Industries", type: "chips", options: ["Fintech", "SaaS", "E-commerce", "Healthcare", "Manufacturing", "Consulting"] },
    { key: "skills", label: "Your top skills", type: "chips", placeholder: "Type a skill and press Enter" },
    { key: "open_to", label: "Open to", type: "chips", options: ["Remote", "Hybrid", "Relocation", "Contract"] },
  ],
  investors: [
    { key: "stage", label: "Stage", type: "single", required: true, options: ["Pre-seed", "Seed", "Series A", "Series B+"] },
    { key: "amount", label: "Amount", type: "single", options: ["Under ₹1 Cr", "₹1-5 Cr", "₹5-25 Cr", "₹25 Cr+", "Under $1M", "$1-5M", "$5M+"] },
    { key: "sector", label: "Sector", type: "text", placeholder: "e.g. B2B SaaS" },
    { key: "traction", label: "Traction to mention", type: "chips", options: ["Revenue", "Paying customers", "Monthly growth", "Users", "Profitability", "Key partnerships"] },
    { key: "investor_type", label: "Investor type", type: "chips", options: ["VC", "Angel", "Family office", "Corporate VC", "Accelerator"] },
  ],
  other: [
    { key: "who", label: "Who to reach", type: "chips", placeholder: "e.g. School principals" },
  ],
};

const filled = (v: GoalFieldValue | undefined) => (Array.isArray(v) ? v.length > 0 : !!String(v ?? "").trim());

export function goalFieldsMissing(goal: string, values: GoalFieldValues): string[] {
  return (GOAL_FIELDS[goal] ?? []).filter((f) => f.required && !filled(values[f.key])).map((f) => f.label);
}

/** Plain-text summary sent to the AI (and kept as goal_details for older readers). */
export function serializeGoalFields(goal: string, values: GoalFieldValues): string {
  return (GOAL_FIELDS[goal] ?? []).filter((f) => filled(values[f.key]))
    .map((f) => `${f.label}: ${Array.isArray(values[f.key]) ? (values[f.key] as string[]).join(", ") : String(values[f.key]).trim()}`)
    .join("; ").slice(0, 1200);
}

export function GoalFieldsForm({ goal, values, onChange, extraSuggestions }: { goal: string; values: GoalFieldValues; onChange: (v: GoalFieldValues) => void; extraSuggestions?: Record<string, string[]> }) {
  const fields = GOAL_FIELDS[goal] ?? [];
  if (!fields.length) return null;
  const set = (k: string, v: GoalFieldValue) => onChange({ ...values, [k]: v });
  const done = fields.filter((f) => filled(values[f.key])).length;
  return (
    <div className="space-y-4 rounded-xl border bg-muted/30 p-4">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>Tap a suggestion or type your own. Fields with * are required.</span>
        <span className={cn("rounded-full px-2 py-0.5 font-medium", done === fields.length ? "bg-sage-muted text-sage" : "bg-muted")}>{done}/{fields.length} filled</span>
      </div>
      {fields.map((f) => {
        const v = values[f.key];
        return (
          <div key={f.key} className="space-y-1.5">
            <Label className="text-sm">{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>
            {f.type === "single" ? (
              <div className="flex flex-wrap gap-1.5">
                {f.options!.map((o) => {
                  const on = v === o;
                  return (
                    <button key={o} type="button" onClick={() => set(f.key, on ? "" : o)} aria-pressed={on}
                      className={cn("inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors",
                        on ? "border-sage bg-sage text-primary-foreground" : "bg-background hover:border-primary/50")}>
                      {on && <Check className="h-3 w-3" />}{o}
                    </button>
                  );
                })}
              </div>
            ) : f.type === "chips" ? (
              <ChipInput value={Array.isArray(v) ? v : []} onChange={(n) => set(f.key, n)} placeholder={f.placeholder ?? "Type and press Enter"}
                suggestions={[...(extraSuggestions?.[f.key] ?? []), ...(f.options ?? [])]} maxSuggestions={10} aria-label={f.label} />
            ) : (
              <Input value={typeof v === "string" ? v : ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder} type={f.type === "link" ? "url" : "text"} />
            )}
          </div>
        );
      })}
    </div>
  );
}
